"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Leaf } from "lucide-react";
export default function SupportOurTeachings() {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return <section className="relative min-h-screen bg-[hsl(30,15%,25%)] overflow-hidden py-16 px-4 sm:px-6 lg:px-8">
      {}
      <div className="absolute inset-0 opacity-20 bg-cover bg-center bg-fixed pointer-events-none" style={{
      backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1444982/img/misty_pine_forest_silhouette.png)"
    }} />

      {}
      <div className="absolute top-1/4 left-1/4 w-16 h-16 opacity-30 animate-[float_8s_ease-in-out_infinite]" style={{
      animation: "floatSeed 8s ease-in-out infinite"
    }}>
        <Leaf className="w-full h-full text-[hsl(140,35%,45%)]" />
      </div>

      {}
      <div className="absolute bottom-1/3 right-1/4 w-12 h-12 opacity-20 animate-[float_8s_ease-in-out_infinite_2s]" style={{
      animation: "floatSeed 8s ease-in-out infinite 2s"
    }}>
        <Leaf className="w-full h-full text-[hsl(30,35%,40%)]" />
      </div>

      <div className="relative max-w-2xl mx-auto">
        <FormProvider className="relative" formId="donation-form-support" sectionName="support-our-teachings" defaultValues={{
        name: "",
        email: "",
        amount: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        amount: {
          required: true,
          min: 1
        },
        message: {
          maxLength: 500
        }
      }} submitText="Donate" successMessage="Thank you for your generous support! Your dana helps keep these teachings freely accessible.">
          <DonationForm mousePosition={mousePosition} />
        </FormProvider>
      </div>

      <style jsx>{`
        @keyframes floatSeed {
          0%,
          100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(30px, -40px) rotate(90deg);
          }
          50% {
            transform: translate(60px, 0) rotate(180deg);
          }
          75% {
            transform: translate(30px, 40px) rotate(270deg);
          }
        }
      `}</style>
    </section>;
}
function DonationForm({
  mousePosition
}: {
  mousePosition: {
    x: number;
    y: number;
  };
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonTransform, setButtonTransform] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    if (buttonRef.current && !isSubmitting) {
      const rect = buttonRef.current.getBoundingClientRect();
      const buttonCenterX = rect.left + rect.width / 2;
      const buttonCenterY = rect.top + rect.height / 2;
      const deltaX = mousePosition.x - buttonCenterX;
      const deltaY = mousePosition.y - buttonCenterY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance < 150) {
        const pullStrength = (150 - distance) / 150;
        const maxPull = 12;
        setButtonTransform({
          x: deltaX / distance * pullStrength * maxPull,
          y: deltaY / distance * pullStrength * maxPull
        });
      } else {
        setButtonTransform({
          x: 0,
          y: 0
        });
      }
    }
  }, [mousePosition, isSubmitting]);
  if (isSubmitted && !submitError) {
    return <div className="bg-[hsl(140,35%,95%)] border-2 border-[hsl(140,35%,45%)] rounded-[2rem] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]" style={{
      animation: "fadeInUp 0.6s ease-out"
    }}>
        <div className="text-center">
          <Leaf className="w-16 h-16 mx-auto mb-4 text-[hsl(140,35%,45%)]" />
          <p className="text-[hsl(0,0%,12%)] text-lg font-display mb-6">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="border-[hsl(140,35%,45%)] text-[hsl(140,35%,45%)] hover:bg-[hsl(140,35%,45%)] hover:text-white transition-all duration-300">
            Make Another Offering
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-[hsl(30,20%,30%)] rounded-[3rem_1rem_3rem_1rem] p-8 sm:p-10 shadow-[0_16px_48px_rgba(0,0,0,0.4)] backdrop-blur-sm" style={{
    animation: "organicRise 0.8s ease-out"
  }}>
      <div className="mb-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-display text-[hsl(0,0%,93%)] mb-3">
          Support Our Teachings
        </h2>
        <p className="text-[hsl(0,0%,80%)] text-base">
          Our teachings are offered freely in the spirit of dana (generosity).
          Your support helps us continue sharing the dharma.
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {}
          <div style={{
          animation: "sequentialAppear 0.5s ease-out 0.2s both"
        }}>
            <FormField control={control} name="name" rules={{
            required: "Name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[hsl(0,0%,93%)] text-base">
                    Your Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your name" {...field} className="bg-[hsl(30,20%,40%)] border-[hsl(30,20%,50%)] text-[hsl(0,0%,93%)] placeholder:text-[hsl(0,0%,60%)] rounded-xl h-12 px-4 focus:border-[hsl(140,35%,45%)] focus:ring-2 focus:ring-[hsl(140,35%,45%)]/30 transition-all duration-300 focus:scale-[1.02] focus:shadow-[0_4px_16px_rgba(76,140,108,0.2)]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div style={{
          animation: "sequentialAppear 0.5s ease-out 0.4s both"
        }}>
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[hsl(0,0%,93%)] text-base">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} className="bg-[hsl(30,20%,40%)] border-[hsl(30,20%,50%)] text-[hsl(0,0%,93%)] placeholder:text-[hsl(0,0%,60%)] rounded-xl h-12 px-4 focus:border-[hsl(140,35%,45%)] focus:ring-2 focus:ring-[hsl(140,35%,45%)]/30 transition-all duration-300 focus:scale-[1.02] focus:shadow-[0_4px_16px_rgba(76,140,108,0.2)]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div style={{
          animation: "sequentialAppear 0.5s ease-out 0.6s both"
        }}>
            <FormField control={control} name="amount" rules={{
            required: "Amount is required",
            min: {
              value: 1,
              message: "Amount must be at least $1"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[hsl(0,0%,93%)] text-base">
                    Donation Amount ($)
                  </FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Enter amount" {...field} className="bg-[hsl(30,20%,40%)] border-[hsl(30,20%,50%)] text-[hsl(0,0%,93%)] placeholder:text-[hsl(0,0%,60%)] rounded-xl h-12 px-4 focus:border-[hsl(140,35%,45%)] focus:ring-2 focus:ring-[hsl(140,35%,45%)]/30 transition-all duration-300 focus:scale-[1.02] focus:shadow-[0_4px_16px_rgba(76,140,108,0.2)]" />
                  </FormControl>
                  <FormDescription className="text-[hsl(0,0%,70%)] text-sm">
                    Any amount is deeply appreciated
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div style={{
          animation: "sequentialAppear 0.5s ease-out 0.8s both"
        }}>
            <FormField control={control} name="message" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[hsl(0,0%,93%)] text-base">
                    Message (Optional)
                  </FormLabel>
                  <FormControl>
                    <textarea placeholder="Share your thoughts or dedication..." {...field} rows={4} className="w-full bg-[hsl(30,20%,40%)] border border-[hsl(30,20%,50%)] text-[hsl(0,0%,93%)] placeholder:text-[hsl(0,0%,60%)] rounded-xl p-4 focus:border-[hsl(140,35%,45%)] focus:ring-2 focus:ring-[hsl(140,35%,45%)]/30 transition-all duration-300 focus:scale-[1.02] focus:shadow-[0_4px_16px_rgba(76,140,108,0.2)] resize-none" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="p-4 bg-[hsl(0,60%,25%)] border-2 border-[hsl(0,60%,40%)] rounded-xl" style={{
          animation: "fadeInUp 0.3s ease-out"
        }}>
              <p className="text-[hsl(0,0%,93%)] text-sm">{submitError}</p>
            </div>}

          {}
          <div style={{
          animation: "sequentialAppear 0.5s ease-out 1s both"
        }}>
            <Button ref={buttonRef} type="submit" disabled={isSubmitting} className="w-full h-14 bg-[hsl(140,35%,45%)] text-white rounded-2xl text-lg font-semibold shadow-[0_0_20px_rgba(76,140,108,0.4)] hover:shadow-[0_0_30px_rgba(76,140,108,0.6)] hover:bg-[hsl(140,40%,38%)] active:bg-[hsl(140,45%,32%)] transition-all duration-300 relative overflow-hidden" style={{
            transform: `translate(${buttonTransform.x}px, ${buttonTransform.y}px)`,
            transition: isSubmitting ? "none" : "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s, box-shadow 0.3s"
          }}>
              {isSubmitting ? "Processing..." : "Donate"}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" style={{
              animation: "shimmer 2s ease-in-out infinite"
            }} />
            </Button>
          </div>
        </div>
      </Form>

      <style jsx>{`
        @keyframes organicRise {
          0% {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes sequentialAppear {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>;
}